import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import ResumeUploader from "@/components/ResumeUploader";
import { submitFreelancerCandidate } from "@/lib/api";

const schema = z.object({
  full_name: z.string().min(2, "Enter candidate's full name"),
  phone: z.string().min(7, "Enter a valid mobile number"),
  email: z.string().email("Enter a valid email"),
  total_experience: z.string().min(1, "Experience is required"),
  current_salary: z.string().optional(),
  expected_salary: z.string().optional(),
  notice_period: z.string().optional(),
  remarks: z.string().optional(),
});

const EMPTY = {
  full_name: "", phone: "", email: "",
  total_experience: "", current_salary: "", expected_salary: "",
  notice_period: "", remarks: "",
};

export default function FreelancerSubmitDialog({ job, open, onOpenChange, onSubmitted }) {
  const [resume, setResume] = useState(null);
  const [resumeError, setResumeError] = useState(null);
  const [done, setDone] = useState(false);
  const [receipt, setReceipt] = useState(null);   // { id, job_title }

  const {
    register, handleSubmit, reset, formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: EMPTY,
  });

  useEffect(() => {
    if (!open) {
      reset(EMPTY);
      setResume(null);
      setResumeError(null);
      setDone(false);
      setReceipt(null);
    }
  }, [open, reset]);

  const onSubmit = async (values) => {
    if (!resume) {
      setResumeError("Please upload the candidate's resume");
      return;
    }
    try {
      const fd = new FormData();
      fd.append("job_id", job.id);
      Object.entries(values).forEach(([k, v]) => fd.append(k, v ?? ""));
      fd.append("resume", resume);
      const created = await submitFreelancerCandidate(fd);
      toast.success("Candidate submitted");
      setReceipt({ id: created?.id, job_title: created?.job_title });
      setDone(true);
      onSubmitted?.();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Could not submit candidate");
    }
  };

  if (!job) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="freelancer-submit-dialog" className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Submit Candidate</DialogTitle>
          <DialogDescription>
            For <span className="font-semibold text-slate-900">{job.title}</span>
            {job.company_name ? ` · ${job.company_name}` : ""} · {job.location}
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div data-testid="freelancer-submit-success" className="py-10 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
            <h3 className="mt-5 font-heading font-bold text-2xl text-slate-900">Candidate submitted!</h3>
            <p className="mt-3 text-sm text-slate-600 max-w-md mx-auto">
              You can view the current status under <strong>My Candidates</strong>.
            </p>
            {receipt?.id && (
              <div data-testid="freelancer-submit-receipt" className="mt-5 inline-block bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-left">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Application ID</div>
                <div className="mt-0.5 font-mono text-xs text-slate-900 select-all break-all">{receipt.id}</div>
              </div>
            )}
            <div className="mt-6 flex justify-center gap-3">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
              <Button
                onClick={() => {
                  reset(EMPTY);
                  setResume(null);
                  setResumeError(null);
                  setReceipt(null);
                  setDone(false);
                }}
                data-testid="freelancer-submit-another"
                className="bg-primary hover:bg-primary-hover text-white"
              >
                Submit another
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} data-testid="freelancer-submit-form" className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Candidate Name *" error={errors.full_name?.message}>
                <Input data-testid="fs-name" placeholder="e.g. Rohit Sharma" {...register("full_name")} />
              </Field>
              <Field label="Mobile *" error={errors.phone?.message}>
                <Input data-testid="fs-phone" placeholder="+91 ..." {...register("phone")} />
              </Field>
              <Field label="Email *" error={errors.email?.message}>
                <Input data-testid="fs-email" type="email" placeholder="candidate@email.com" {...register("email")} />
              </Field>
              <Field label="Experience *" error={errors.total_experience?.message}>
                <Input data-testid="fs-experience" placeholder="e.g. 3 years" {...register("total_experience")} />
              </Field>
              <Field label="Current Salary">
                <Input data-testid="fs-current-salary" placeholder="e.g. ₹3.5 LPA" {...register("current_salary")} />
              </Field>
              <Field label="Expected Salary">
                <Input data-testid="fs-expected-salary" placeholder="e.g. ₹5 LPA" {...register("expected_salary")} />
              </Field>
              <Field label="Notice Period">
                <Input data-testid="fs-notice" placeholder="e.g. 30 days" {...register("notice_period")} />
              </Field>
            </div>

            <Field label="Remarks">
              <Textarea data-testid="fs-remarks" rows={3} placeholder="Any notes for the recruiter…" {...register("remarks")} />
            </Field>

            <div>
              <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Resume *</Label>
              <div className="mt-1.5">
                <ResumeUploader
                  value={resume}
                  onChange={(f, err) => { setResume(f); setResumeError(err); }}
                  error={resumeError}
                  testId="fs-resume"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button
                type="submit"
                data-testid="fs-submit"
                disabled={isSubmitting}
                className="bg-primary hover:bg-primary-hover text-white"
              >
                {isSubmitting ? (
                  <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Submitting…</span>
                ) : (
                  <span className="inline-flex items-center gap-2"><Send className="w-4 h-4" />Submit</span>
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, error, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{label}</Label>
      {children}
      {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
